import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { CallReportModal } from '../components/call-reports/CallReportModal';
import { ViewCallReportModal } from '../components/call-reports/ViewCallReportModal';
import { searchCallReports, deleteCallReport } from '../services/callReportService';
import { useProfileStore } from '../store/useProfileStore';
import { isCompanyShopSuperAdmin } from '../types/profile';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import type { RetellCallReport } from '../types/callReport';
import type { Column } from '../components/shared/DataTable';

const columns: Column<RetellCallReport>[] = [
  { key: 'agentId', label: 'Agent ID' },
  { key: 'name', label: 'Name' },
  {
    key: 'scoreCategories',
    label: 'Categories',
    render: (item) =>
      item.scoreCategories.length === 0 ? '—' : (
        <div className="flex flex-col gap-0.5">
          {item.scoreCategories.map((c, i) => (
            <div key={i} className="text-sm">{c.title} ({c.criteria?.length || 0})</div>
          ))}
        </div>
      ),
  },
];

function RowMenu({ item, canEdit, onView, onEdit, onDelete }: { item: RetellCallReport; canEdit: boolean; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          {canEdit ? (
            <>
              <button
                onClick={() => { setOpen(false); onEdit(); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => { setOpen(false); onDelete(); }}
                className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={() => { setOpen(false); onView(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              View
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CallReportPage() {
  const location = useLocation();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const canEdit = isCompanyShopSuperAdmin(activeProfile);
  const [data, setData] = useState<RetellCallReport[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [modalReport, setModalReport] = useState<RetellCallReport | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [viewReport, setViewReport] = useState<RetellCallReport | null>(null);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);

  useEffect(() => {
    if (location.pathname !== '/call-report') return;
    setBreadcrumb([{ label: 'Call Reports' }]);
  }, [location.pathname, setBreadcrumb]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCallReports({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/call-report', fetch);
    return () => unregisterRefreshHandler('/call-report');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  function openCreate() {
    setModalReport(undefined);
    setShowModal(true);
  }

  function openEdit(item: RetellCallReport) {
    setModalReport(item);
    setShowModal(true);
  }

  async function handleDelete(id: string) {
    try {
      await deleteCallReport(id);
      fetch();
    } catch {
      // error modal handled by apiService
    }
  }

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title="Call Reports"
            columns={columns}
            data={data}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            onAdd={canEdit ? openCreate : undefined}
            alignTop
            actions={(item) => (
              <RowMenu
                item={item}
                canEdit={canEdit}
                onView={() => setViewReport(item)}
                onEdit={() => openEdit(item)}
                onDelete={() => handleDelete(item.id)}
              />
            )}
          />
          {showModal && (
            <CallReportModal
              report={modalReport}
              onClose={() => setShowModal(false)}
              onSuccess={() => { setShowModal(false); fetch(); }}
            />
          )}
          {viewReport && (
            <ViewCallReportModal
              report={viewReport}
              onClose={() => setViewReport(null)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
